import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    if (mediaQueryLarge.matches) {
      const storedDesktop = sessionStorage.getItem('product-grid-view-desktop');
      if (!storedDesktop) {
        sessionStorage.setItem('product-grid-view-desktop', 'zoom-out');
        this.#setLayout('zoom-out');
        const zoomOutOption = this.querySelector('input[name="grid"][value="zoom-out"]');
        if (zoomOutOption instanceof HTMLInputElement) {
          zoomOutOption.checked = true;
        }
      }
    }

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const storedLayout = event.matches
      ? (sessionStorage.getItem('product-grid-view-desktop') || 'zoom-out')
      : (sessionStorage.getItem('product-grid-view-mobile') || 'default');
    const targetElement = event.matches
      ? this.querySelector(`input[name="grid"][value="${storedLayout}"]`)
      : this.querySelector(`input[name="grid-mobile"][value="${storedLayout}"]`);

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout(storedLayout);
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
